/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable arrow-body-style */
// Package Imports
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';

// Models
import { AuthMetaData, AuthUser, UserSessionData } from '../../models';

// Services
import { AppConfigService } from '../app-config/app-config.service';
import { SessionService } from '../session/session.service';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  private manager = new UserManager(this.getClientSettings());
  private currentUser: User;
  private userSession: UserSessionData;

  constructor(private readonly router: Router,
              private readonly appConfigService: AppConfigService,
              private readonly sessionService: SessionService) {
    this.getUser();

    this.manager.events.addAccessTokenExpiring(() => {
      this.manager.signinSilent()
        .then(user => {
          this.currentUser = user;
        });
    });
  }

  async isLoggedIn(): Promise<boolean> {
    await this.getUser();

    return this.currentUser != null && !this.currentUser.expired;
  }

  async getClaims(): Promise<any> {
    await this.getUser();

    return this.currentUser.profile;
  }

  async getAuthorizationHeaderValue(): Promise<string> {
    const promise = new Promise<string>((resolve) => {
      this.getUser().then(() => {
        resolve(`${this.currentUser.token_type} ${this.currentUser.access_token}`);
      });
    });
    return promise;
  }

  async getToken(): Promise<string> {
    await this.getUser();

    return this.currentUser.access_token;
  }

  startAuthentication(): Promise<void> {
    const metaData: AuthMetaData = this.sessionService.getSessionData(this.sessionService.sessionKey_AuthMetaData);

    if (metaData && metaData.hint) {
      const args: any = {
        login_hint: metaData.hint
      };

      return this.manager.signinRedirect(args);
    }
    else {
      return this.manager.signinRedirect();
    }
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback().then(user => {
      this.currentUser = user;
      this.userSession = this.getUserSession;

      if (!this.userSession) {
        this.userSession = new UserSessionData();
      }

      if (!this.userSession.user) {
        this.userSession.user = new AuthUser();
      }

      this.userSession.user.id = this.currentUser.profile.sub;
      this.sessionService.storeSessionData(this.sessionService.sessionKey_UserSession, this.userSession);
    });
  }

  async logout(): Promise<void> {
    const isLoggedIn = await this.isLoggedIn();

    if (!isLoggedIn) {
      this.sessionService.clearSession();
      this.router.navigate(['/unauthorized/401']);

      return;
    }

    this.manager.signoutRedirectCallback().then(() => {
      this.sessionService.clearSession();
    });

    return this.manager.signoutRedirect();
  }

  logoutAndClose(): Promise<void> {
    const settings = this.appConfigService.getData('identityServer');
    const portStr = this.getPortStr();
    const post_logout_redirect_uri =
    `${window.location.protocol}//${window.location.hostname}${portStr}${settings.post_logout_redirect_close_uri}`;

    this.manager.signoutRedirectCallback().then(() => {
      this.sessionService.clearSession();
    });

    return this.manager.signoutRedirect({ post_logout_redirect_uri });
  }

  private getUser(): Promise<boolean> {
    const promise = new Promise<boolean>((resolve) => {
      this.manager.getUser().then(user => {
        this.currentUser = user;
        resolve(true);
      });
    });

    return promise;
  }

  private getClientSettings(): UserManagerSettings {
    const settings = this.appConfigService.getData('identityServer');
    const portStr = this.getPortStr();

    // These properties are readonly, so we can't type cast until we populate those values
    settings.redirect_uri = `${window.location.protocol}//${window.location.hostname}${portStr}${settings.redirect_uri}`;
    settings.post_logout_redirect_uri =
      `${window.location.protocol}//${window.location.hostname}${portStr}${settings.post_logout_redirect_uri}`;
    settings.silent_redirect_uri = `${window.location.protocol}//${window.location.hostname}${portStr}${settings.silent_redirect_uri}`;

    // Allow time skew of 10 minutes (in seconds)
    settings.clockSkew = 600;

    return settings as UserManagerSettings;
  }

  private get getUserSession(): UserSessionData {
    return new UserSessionData();
  }

  private getPortStr(): string {
    let portStr = '';
    if (window.location.port && window.location.port !== '' && window.location.port !== '0') {
      portStr = `:${window.location.port}`;
    }

    return portStr;
  }
}
